import React from 'react';
import { logger } from '@/utils/logger';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Edit, Package, Plus, X, Trash2 } from 'lucide-react';

type Producer = Database['public']['Tables']['producers']['Row'] & {
  email?: string; // Added for joined profile email data
};

interface InviteFormData {
  email: string;
  companyName: string;
}

/**
 * Helper to call admin Netlify Functions with auth token.
 */
async function adminFetch(path: string, options: RequestInit) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...options.headers,
    },
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Request failed');
  return data;
}

export function ProducersList() {
  const [producers, setProducers] = React.useState<Producer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = React.useState(false);
  const [inviteForm, setInviteForm] = React.useState({
    email: '',
    companyName: ''
  });
  const [isInviting, setIsInviting] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    fetchProducers();
  }, []);

  async function fetchProducers() {
    try {
      const { data, error } = await supabase
        .from('producers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        setProducers(data);
      } else {
        setProducers([]);
      }
    } catch (err) {
      logger.error('Error fetching producers:', err);
      setError('Failed to load producers');
    } finally {
      setLoading(false);
    }
  }

  const handleInvite = async (inviteData: InviteFormData) => {
    try {
      setIsInviting(true);

      await adminFetch('/api/admin-invite-producer', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteData.email,
          companyName: inviteData.companyName,
        }),
      });

      setIsInviteModalOpen(false);
      setInviteForm({ email: '', companyName: '' });
      toast.success('Producer invitation sent successfully!');
      fetchProducers();
    } catch (err) {
      logger.error('Error inviting producer:', err);
      toast.error(`Failed to invite producer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsInviting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleInvite({ email: inviteForm.email, companyName: inviteForm.companyName });
  };

  const toggleApprovalStatus = async (producerId: string, currentStatus: boolean) => {
    try {
      await adminFetch('/api/admin-manage-producer', {
        method: 'PATCH',
        body: JSON.stringify({
          producerId,
          isApproved: !currentStatus,
        }),
      });

      // Update local state immediately
      setProducers(prevProducers =>
        prevProducers.map(producer =>
          producer.id === producerId
            ? { ...producer, is_approved: !currentStatus }
            : producer
        )
      );

      const action = currentStatus ? 'unapproved' : 'approved';
      toast.success(`Producer ${action} successfully`);
    } catch (error) {
      logger.error('Error toggling approval status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to update producer status: ${errorMessage}`);
    }
  };

  const handleRemoveProducer = async (producerId: string, producerName: string) => {
    if (!confirm(`Are you sure you want to remove ${producerName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(true);

      await adminFetch('/api/admin-manage-producer', {
        method: 'DELETE',
        body: JSON.stringify({ producerId }),
      });

      toast.success(`Producer ${producerName} has been removed`);
      fetchProducers();
    } catch (err) {
      logger.error('Error removing producer:', err);
      toast.error(`Failed to remove producer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Producers</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Invite Producer
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-medium">Invite New Producer</h2>
              <button
                onClick={() => setIsInviteModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="producer@example.com"
                />
              </div>
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  required
                  value={inviteForm.companyName}
                  onChange={(e) => setInviteForm({ ...inviteForm, companyName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="Company Name"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isInviting}
                  className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {isInviting ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6">
                Producer
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                Contact
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                Website/Social
              </th>
              <th scope="col" className="px-3 py-3.5 text-center text-sm font-medium text-gray-900">
                Status
              </th>
              <th scope="col" className="px-3 py-3.5 text-center text-sm font-medium text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {producers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No producers found
                </td>
              </tr>
            ) : (
              producers.map((producer) => (
                <tr key={producer.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                    <div className="flex items-center">
                      {producer.logo_url ? (
                        <img
                          src={producer.logo_url}
                          alt=""
                          className="h-12 w-12 rounded-md object-contain bg-gray-50"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-md bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-500 font-medium text-lg">
                            {producer.company_name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="font-medium text-gray-900">
                          {producer.company_name}
                        </div>
                        <div className="text-gray-500 text-xs mt-1">
                          Joined {new Date(producer.created_at).toLocaleDateString()}
                        </div>
                        {producer.description && (
                          <div className="text-gray-500 text-xs mt-1 max-w-xs truncate">
                            {producer.description.replace(/<[^>]*>/g, '')}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <div className="font-medium">
                      {producer.email ? (
                        <div className="text-xs">{producer.email}</div>
                      ) : (
                        <span className="italic">No contact email</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <div className="space-y-1">
                      {producer.website_url && (
                        <div className="text-xs">
                          <a href={producer.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Website
                          </a>
                        </div>
                      )}
                      {producer.facebook_url && (
                        <div className="text-xs">
                          <a href={producer.facebook_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Facebook
                          </a>
                        </div>
                      )}
                      {producer.instagram_url && (
                        <div className="text-xs">
                          <a href={producer.instagram_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Instagram
                          </a>
                        </div>
                      )}
                      {producer.twitter_url && (
                        <div className="text-xs">
                          <a href={producer.twitter_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Twitter
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-center">
                    <span 
                      data-testid={`approve-${producer.id}`}
                      className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded cursor-pointer ${
                        producer.is_approved
                          ? 'text-green-700 bg-green-100 hover:bg-green-200'
                          : 'text-red-700 bg-red-100 hover:bg-red-200'
                      }`}
                      onClick={() => toggleApprovalStatus(producer.id, producer.is_approved)}
                    >
                      {producer.is_approved ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approved
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Not Approved
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-center">
                    <div className="flex justify-center space-x-2">
                      <Link
                        to={`/admin/producers/${producer.id}/edit`}
                        className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Link>
                      <Link
                        to={`/admin/producers/${producer.id}/products`}
                        className="text-green-600 hover:text-green-900 inline-flex items-center"
                      >
                        <Package className="h-4 w-4 mr-1" />
                        Products
                      </Link>
                      <button
                        className="icon-button delete"
                        onClick={() => handleRemoveProducer(producer.id, producer.company_name)}
                        disabled={isDeleting}
                        title="Remove Producer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
